export const THUMBNAIL_SCALE_KEY = "manageArtworkImageThumbnailScale";

export const THUMBNAIL_SCALE_MIN = 1;
export const THUMBNAIL_SCALE_MAX = 6;
export const THUMBNAIL_SCALE_STEP = 1;

// Default keeps thumbnails at a comfortable mid size.
export const DEFAULT_THUMBNAIL_SCALE = 3;

// Full-screen preference is persisted the same way as the thumbnail scale
// (a single localStorage-backed number via useSliderPosition), where 1 means
// the modal opens full screen and 0 means it opens at its default size.
export const FULLSCREEN_KEY = "manageArtworkImageFullscreen";
export const FULLSCREEN_ON = 1;
export const FULLSCREEN_OFF = 0;
export const DEFAULT_FULLSCREEN = FULLSCREEN_OFF;

// Map the persisted full-screen flag to the Modal's `scale` prop.
export const getModalScaleForFullscreen = isFullscreen =>
  Number(isFullscreen) === FULLSCREEN_ON ? "fullscreen" : "md";

// Each scale maps to the thumbnail's height (px). Every thumbnail shares this
// height and its width follows the image's natural aspect — so portrait and
// landscape images line up on a common baseline while keeping their
// orientation. Larger scale = taller thumbnails; with flex-wrap the row count
// follows from how many fit the container.
const SCALE_TO_SIZE = {
  1: 80,
  2: 110,
  3: 140,
  4: 170,
  5: 210,
  6: 260,
};

export const clampThumbnailScale = scale => {
  if (Number.isNaN(Number(scale))) {
    return DEFAULT_THUMBNAIL_SCALE;
  }

  return Math.min(THUMBNAIL_SCALE_MAX, Math.max(THUMBNAIL_SCALE_MIN, scale));
};

export const getThumbnailSizeForScale = scale => {
  const clamped = clampThumbnailScale(scale);
  return SCALE_TO_SIZE[clamped] ?? SCALE_TO_SIZE[DEFAULT_THUMBNAIL_SCALE];
};

// Given a fixed height and an image aspect ratio (naturalWidth /
// naturalHeight), return box dimensions that keep every thumbnail the same
// height while the width follows the image's orientation. Falls back to a
// square while the ratio is still unknown.
export const getThumbnailDimensions = (size, ratio) => {
  if (!ratio || ratio <= 0) {
    return { width: size, height: size };
  }

  return { width: Math.round(size * ratio), height: size };
};

// Extract the trailing number from a filename, ignoring the extension.
// "something_001.jpg" -> 1, "something_3.jpg" -> 3, "no-number.jpg" -> null
export const getTrailingNumber = filename => {
  if (typeof filename !== "string") {
    return null;
  }

  const withoutExtension = filename.replace(/\.[^./\\]+$/, "");
  const match = withoutExtension.match(/(\d+)$/);
  return match ? Number(match[1]) : null;
};

// Order files by the trailing number in their name so a bulk upload keeps a
// natural sequence (1, 2, 10) regardless of the order the OS returned them.
// Files without a trailing number keep their original relative order and sort
// after the numbered ones. Does not mutate the input.
export const sortFilesByTrailingNumber = files =>
  [...files]
    .map((file, index) => ({
      file,
      index,
      number: getTrailingNumber(file?.name),
    }))
    .sort((a, b) => {
      const aHasNumber = a.number !== null;
      const bHasNumber = b.number !== null;

      if (aHasNumber && bHasNumber) {
        return a.number - b.number || a.index - b.index;
      }
      if (aHasNumber) {
        return -1;
      }
      if (bHasNumber) {
        return 1;
      }
      return a.index - b.index;
    })
    .map(entry => entry.file);

// Pure move-style reorder used by the drag-and-drop handlers.
export const reorder = (list, fromIndex, toIndex) => {
  if (
    fromIndex == null ||
    toIndex == null ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return list;
  }

  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

// Whether the working image order differs from the saved/baseline order.
// Compares the relative order of images present in both lists (by archiveId),
// so adding or removing images doesn't register as a reorder — only an actual
// change in sequence does. This is deliberately independent of the `order`
// field, which is not guaranteed to be a contiguous 1..N sequence (newly
// uploaded images have no order yet and deletions leave gaps).
export const isImageOrderChanged = (currentImages = [], baselineImages = []) => {
  const currentIds = currentImages.map(image => image?.archiveId);
  const baselineIds = baselineImages.map(image => image?.archiveId);
  const baselineSet = new Set(baselineIds);
  const currentSet = new Set(currentIds);

  const commonInCurrent = currentIds.filter(id => baselineSet.has(id));
  const commonInBaseline = baselineIds.filter(id => currentSet.has(id));

  return commonInCurrent.some((id, index) => id !== commonInBaseline[index]);
};

// Fold a freshly loaded image list into the one on screen. An unsaved reorder
// is kept, with new images appended; otherwise the server list replaces it, so
// an upload shows up without discarding a drag the user hasn't saved.
export const mergeIncomingImages = (
  currentImages = [],
  incomingImages = [],
) => {
  if (!isImageOrderChanged(currentImages, incomingImages)) {
    return incomingImages;
  }

  const incomingById = new Map(
    incomingImages.map(image => [image?.archiveId, image]),
  );
  const currentIds = new Set(currentImages.map(image => image?.archiveId));
  const kept = currentImages.map(
    image => incomingById.get(image?.archiveId) ?? image,
  );
  const added = incomingImages.filter(
    image => !currentIds.has(image?.archiveId),
  );

  return [...kept, ...added];
};
